import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import highlight1 from 'stile-au/static/highlight-1.svg';
import underline14Mobile from 'stile-au/static/underline-14-mobile.png';
import underline14 from 'stile-au/static/underline-14.png';
import playButtonMobile from 'stile-shared/static/play-button-mobile.svg';
import playButton from 'stile-shared/static/play-button.svg';
import underline23Mobile from 'stile-shared/static/underline-23-mobile.svg';
import underline23 from 'stile-shared/static/underline-23.svg';
import styled from 'styled-components';

import iconPage from 'stile-shared/assets/icons/icon_page.svg';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import { Card } from 'stile-shared/src/components/2024/common/Card';
import { Icon } from 'stile-shared/src/components/2024/common/Icon';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import VideoIcon from 'stile-shared/src/components/2024/common/VideoIcon';
import BottomCTA from 'stile-shared/src/components/2024/layout/BottomCTA';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { TopSection } from 'stile-shared/src/components/2024/layout/RegionBasedTopSection';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

export { Head } from 'templates/2024/Head';

const VicTopSection = styled(TopSection)`
  .content {
    padding-bottom: 120px;
    padding-top: 80px;
    .--left {
      font-weight: 600;
      align-self: stretch;
      h1 {
        margin: 0px 0px 48px;
      }
      ul {
        width: 100%;
        list-style-type: disc !important;
        padding-left: 20px !important;
        margin: 0;
      }
      li {
        display: list-item !important;
        margin-top: 10px;
      }
    }
    .--right {
      width: 50%;
      .gatsby-image-wrapper {
        margin-top: 0px;
        border-radius: 32px;
        aspect-ratio: 1;
      }
    }
  }
  @media (max-width: 768px) {
    padding-top: 4px;
    background-color: var(--stile-grey-1);
    .content {
      padding-top: 0;
      padding-bottom: 0;
      margin-right: -5px;
        h1 {
          margin: 0px 0px 30px;
        }
        .--right {
          left: 0;
          right: 0;
          height: 390px;
          width: calc(100% + 50px);
          position: relative;
          margin: 55px -90px 0 -30px;

          .gatsby-image-wrapper {
            height: 390px;
            width: calc(100% + 20px) !important;
            border-radius: 0;
            margin-top: 0;
            aspect-ratio: default;
            box-shadow: none important!;
          }
        }
      }
    }
  }
`;

const VicLayout = styled<typeof PageLayout>(PageLayout)`
  p {
    margin: 12px 0px;
  }
  .iconButton {
    padding-left: 16px !important;
  }
  .green-highlight {
    color: var(--stile-grey-8);
    display: inline-block;
    position: relative;
    z-index: 1;
    :before {
      content: '';
      display: block;
      position: absolute;
      bottom: 1px;
      left: -10px;
      width: 298.38px;
      height: 67.41px;
      background-repeat: no-repeat;
      background-position: top left;
      background-size: cover;
      background-image: url('${highlight1}');
      z-index: -1;
    }
  }
  .underline-14,
  .underline-23 {
    display: inline-block;
    position: relative;
    :after {
      bottom: 5px;
      content: '';
      display: block;
      position: absolute;
      background-repeat: no-repeat;
      background-position: top left;
      background-size: cover;
    }
  }
  .underline-14:after {
    left: 5px;
    width: 100%;
    height: 8px;
    background-image: url('${underline14}');
  }
  .underline-23:after {
    left: -3px;
    width: 116px;
    height: 10px;
    background-image: url('${underline23}');
  }
  @media (max-width: 768px) {
    .green-highlight:before {
      bottom: 0;
      left: -5px;
      height: 41.11px;
      width: 182px;
    }
    .underline-14:after {
      background-image: url('${underline14Mobile}');
      bottom: -22px;
      left: -3px;
      width: 135px;
      height: 44px;
    }

    .underline-23:after {
      background-image: url('${underline23Mobile}');
      bottom: -1px;
      left: -2px;
      width: 75px;
      height: 8px;
    }
    .header-wrapper {
      background-color: var(--stile-grey-1);
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin: 150px 140px 0;
  gap: 150px;
  @media (max-width: 768px) {
    margin: 60px 25px 0;
    gap: 60px;
  }
`;

const DarkBanner = styled.div`
  margin-top: 150px;
  width: 100%;
  background-color: var(--stile-grey-8);
  padding: 100px 140px;
  @media (max-width: 768px) {
    margin-top: 60px;
    padding: 60px 25px;
  }
`;

const CurriculumSection = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  .container,
  .reverse-container,
  .dark {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    max-width: 1200px;
    width: 100%;
    gap: 60px;
  }
  .dark {
    color: var(--white);
  }
  .highlight {
    font-weight: 600;
  }

  .left,
  .left-img,
  .right {
    flex: 1;
    max-width: 566px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .left-img {
    margin-top: 35px;
    .video-wrapper {
      position: relative;
      width: 100%;
      display: flex;
      justify-content: center;
    }
    .footer-text {
      color: var(--stile-green-3);
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      margin-top: 15px;
      text-align: left;
      width: 100%;
      align-self: flex-start;
    }
    .playButton {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 1;
    }
  }
  .left-text {
    max-width: 498px;
  }
  .right-text {
    max-width: 468px;
  }
  .right-text,
  .left-text {
    flex: 1;
    a {
      margin-top: 20px;
    }
  }
  h2 {
    margin-bottom: 32px;
    margin-top: 0;
  }
  @media (max-width: 768px) {
    .container {
      flex-direction: column-reverse;
      align-items: flex-start;
      gap: 18px;
    }
    .reverse-container,
    .dark {
      flex-direction: column;
      align-items: flex-start;
    }
    .dark {
      gap: 32px;
    }
    .reverse-container {
      gap: 20px;
    }
    .left-img {
      margin-top: 12px;
    }
    .left-text {
      a {
        margin-top: 16px;
      }
    }
    .right-text {
      a {
        margin-top: 8px;
      }
    }
    h2 {
      margin-bottom: 28px;
    }
  }
`;

const CaseStudiesSection = styled<typeof Section>(Section)`
  h2 {
    text-align: left !important;
    grid-column: 1 / span 12 !important;
  }
  h4 {
    margin: 0px;
    text-align: left !important;
  }
  .content {
    padding-top: 0 !important;
    margin: 0 !important;
  }
  .grid-layout {
    margin-top: 0 !important;
  }
  .cards-container {
    grid-column: span 12;
    .card {
      border-radius: 16px;
      box-shadow: 0px 41.958px 25.37px 0px rgba(0, 0, 0, 0.04),
        0px 18.54px 18.54px 0px rgba(0, 0, 0, 0.06), 0px 4.879px 10.733px 0px rgba(0, 0, 0, 0.07);
      @media (max-width: 768px) {
        margin-bottom: 20px !important;
        &:last-child {
          margin-bottom: 60px !important;
        }
      }
    }
  }
`;

const CTA = styled.div`
  padding-bottom: 150px;
  @media (max-width: 768px) {
    padding-bottom: 0;
    .setUpTrialButton {
      margin-top: 10px;
    }
    .--copy {
      margin-top: 0 !important;
    }
    p {
      margin: 12px 0px !important;
    }
    .content {
      border-radius: 20px !important;
    }
  }
`;

const StyledImageWrapper = styled.div`
  display: flex;
  min-width: 267px;
  justify-content: center;
  .greyBorder {
    border-color: var(--stile-grey-1);
    border-style: solid;
    border-width: 1px;
  }
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.2);
  }

  .gatsby-image-wrapper,
  .overlay {
    border-radius: 32px;
  }
  .cover,
  .withShadow,
  .greyBorder,
  .overlay {
    box-shadow: 0px 41.958px 25.37px 0px rgba(0, 0, 0, 0.04),
      0px 18.54px 18.54px 0px rgba(0, 0, 0, 0.06), 0px 4.879px 10.733px 0px rgba(0, 0, 0, 0.07);
  }
  @media (max-width: 768px) {
    .gatsby-image-wrapper,
    .overlay {
      border-radius: 20px !important;
    }
    .cover {
      box-shadow: none;
    }
  }
`;

function VicLandingPage(props: PageProps) {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();

    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <VicLayout {...props}>
      <VicTopSection>
        <div className="content">
          <div className="--left">
            <h1>
              The world-class
              <br />
              science curriculum
              <br />
              for <span className="underline-14">Victoria</span>
            </h1>
            <ul>
              {isMobile ? (
                <>
                  <li>Always up-to-date.</li>
                  <li>Seamlessly integrated.</li>
                  <li>Powers teachers, empowers students.</li>
                </>
              ) : (
                <>
                  <li>Real-world relevant science.</li>
                  <li>Updated for the new Victorian curriculum standards.</li>
                  <li>Ready to make yours.</li>
                </>
              )}
            </ul>
          </div>
          <div className="--right">
            <StyledImageWrapper>
              <StaticImage
                className="cover"
                src="../../../../stile-shared/assets/images/resources/vic_landing.png"
                alt="Students working together"
                placeholder="none"
                loading="eager"
              />
            </StyledImageWrapper>
          </div>
        </div>
      </VicTopSection>
      <Content>
        <CurriculumSection>
          <div className="container">
            <div className="left-img">
              <Link
                openInNewTab={true}
                className="video-wrapper"
                url={
                  'http://bigmarker.com/stile-education1/the-victorian-curriculum-2-0-what-s-changed'
                }
              >
                <StyledImageWrapper>
                  <StaticImage
                    className="withShadow"
                    src="../../../../stile-shared/assets/images/resources/vic_curriculum_cover.png"
                    alt="Stile classroom"
                    quality={100}
                  />
                </StyledImageWrapper>
                {isMobile ? (
                  <img src={playButtonMobile} alt="Play Button" className="playButton" />
                ) : (
                  <img src={playButton} alt="Play Button" className="playButton" />
                )}
              </Link>
              <div className="footer-text">Free on-demand PL</div>
            </div>
            <div className="right-text">
              <h2>The curriculum is changing. We’ve got your back.</h2>
              <p>
                Stile meets and exceeds the requirements of the updated Victorian science curriculum
                for Years 7 - 10. Real-world, relevant, phenomena driven units are as interesting to
                teach as they are to learn.
              </p>
              <p>
                Curious about the updates to the Victorian Science Curriculum? Watch{' '}
                <span className="highlight">Alex Russell</span>, our curriculum expert, as she
                breaks down the key changes ahead and shows how Stile makes the transition seamless
                and stress-free.
              </p>
              <Button
                className="iconButton"
                newTab={true}
                href="https://stileeducation.com/au/resources/victorian-curriculum-2026/"
                label={
                  <>
                    <Icon src={iconPage} iconSize="20px" style={{ marginRight: 6 }} />
                    <span>Download implementation guide</span>
                  </>
                }
              />
            </div>
          </div>
        </CurriculumSection>
        <CurriculumSection>
          <div className="reverse-container">
            <div className="left-text">
              <h2>
                Make Stile your <span className="underline-23">own</span>
              </h2>
              {isMobile ? (
                <>
                  <p>
                    Stile is designed to be customised—by your teachers, for your students, aligned
                    with your school. It’s built to speak your language, not the other way around.
                  </p>
                  <p>
                    So, what does this look like in action? Imagine having an extra teacher and
                    research assistant right by your side—that’s Stile. A curriculum that adapts to
                    your needs, a Scope and Sequence tailored to your vision, with seamless updates
                    running in the background to keep your lessons up-to-date.
                  </p>
                  <p>
                    A world-class science curriculum. At your fingertips. As unique as your school.
                    Neat, huh?
                  </p>
                </>
              ) : (
                <>
                  <p>
                    When your teachers bring their favourite resources and interweave them with
                    ours, the real magic happens. We work with you to customise Stile, incorporating
                    your unique programs, equipment, expertise, and more.
                  </p>
                  <p>
                    Stile becomes your world-class science curriculum, unique to your school and
                    your vision. All backed by our expert team behind the scenes.
                  </p>
                </>
              )}
            </div>
            <div className="right">
              <StyledImageWrapper>
                <StaticImage
                  className="greyBorder"
                  key={0}
                  src="../../../../stile-shared/assets/images/resources/make_your_own.png"
                  alt="Make Stile your own"
                />
              </StyledImageWrapper>
            </div>
          </div>
        </CurriculumSection>
      </Content>
      <DarkBanner>
        <CurriculumSection>
          <div className="dark">
            <div className="left-text">
              <h2>
                The <span className="green-highlight">perfect mix</span> of print, practical &
                digital
              </h2>
              <p>
                Get the best of all worlds. With a blend of print, hands-on activities, and digital
                tools, students are completely tuned in (and always on their toes!). With pens in
                hand, simulations to explore, and exciting challenges ahead, every lesson is fully
                aligned with the Victorian Curriculum—keeping teachers supported and students hungry
                for more.
              </p>
              <Button
                newTab={true}
                href="http://stileapp.com/go/perfectmix"
                label={
                  <>
                    <span>See for yourself</span>
                  </>
                }
              />
            </div>
            <div className="left">
              <StaticImage
                className="withShadow"
                key={0}
                src="../../../../stile-shared/assets/images/resources/perfect_mix.png"
                alt="Perfect Mix"
              />
            </div>
          </div>
        </CurriculumSection>
      </DarkBanner>
      <Content>
        <CurriculumSection>
          <div className="container">
            <div className="left">
              <StyledImageWrapper>
                <VideoIcon
                  style="secondary"
                  image={
                    <>
                      <StaticImage
                        className="withShadow"
                        src="../../../../stile-shared/assets/images/resources/michelle_cover.jpg"
                        alt="thumbnail from a video"
                      />
                      <div className="overlay" />
                    </>
                  }
                  youtubeUrl="https://youtu.be/QQEbxNlLD9Y?si=n6ujkooOUcW9s7zS"
                />
              </StyledImageWrapper>
            </div>
            <div className="right-text">
              <h2>Hear from science teacher, Michelle</h2>
              <p>
                With Stile, you’re in control, and the options are limitless. From sparking fresh
                ideas, helping students catch up, to powering full-class discussion—Stile flexes to
                fit exactly what your classroom needs, all while keeping you in the driver’s seat. 
              </p>
              <p>Here’s why Michelle chooses Stile year after year.</p>
            </div>
          </div>
        </CurriculumSection>
        <CaseStudiesSection
          headingColor="--stile-grey-8"
          headingContent={<h4>Read more Victorian school case studies</h4>}
          textColor="--stile-grey-8"
        >
          <Grid className="cards-container">
            <Card
              headerImage={
                <StaticImage
                  src="../../../../stile-shared/assets/images/resources/montmorency_secondary_college.png"
                  alt="Montmorency Secondary College"
                />
              }
              description={
                <>
                  <h6 className="h7">Montmorency Secondary College</h6>
                  <h6>More time for teachers to engage meaningfully with students.</h6>
                </>
              }
              button={
                <Button
                  className="iconButton"
                  style="secondary"
                  key={0}
                  href="https://stileapp.com/go/stile_x_montmorency_sc"
                  external={true}
                  newTab={true}
                  size="small"
                  label={
                    <div>
                      <Icon src={iconPage} iconSize="20px" style={{ marginRight: 6 }} />
                      Download PDF
                    </div>
                  }
                />
              }
            />
            <Card
              headerImage={
                <StaticImage
                  src="../../../../stile-shared/assets/images/resources/covenant_college.png"
                  alt="Covenant College"
                />
              }
              description={
                <>
                  <h6 className="h7">Covenant College</h6>
                  <h6>Improvements in students’ fundamental learning skills.</h6>
                </>
              }
              button={
                <Button
                  className="iconButton"
                  style="secondary"
                  key={0}
                  href="https://stileapp.com/go/stile_x_covenant_college"
                  external={true}
                  newTab={true}
                  size="small"
                  label={
                    <div>
                      <Icon src={iconPage} iconSize="20px" style={{ marginRight: 6 }} />
                      Download PDF
                    </div>
                  }
                />
              }
            />

            <Card
              headerImage={
                <StaticImage
                  src="../../../../stile-shared/assets/images/resources/galen_catholic_college.png"
                  alt="Galen Catholic College"
                />
              }
              description={
                <>
                  <h6 className="h7">Galen Catholic College</h6>
                  <h6>Evidence-based professional learning that empowers teachers.</h6>
                </>
              }
              button={
                <Button
                  style="secondary"
                  key={0}
                  href="https://stileapp.com/go/stile_x_galen_catholic_college"
                  external={true}
                  newTab={true}
                  size="small"
                  label={
                    <div>
                      <Icon src={iconPage} iconSize="20px" style={{ marginRight: 6 }} />
                      Download PDF
                    </div>
                  }
                />
              }
            />
          </Grid>
        </CaseStudiesSection>
      </Content>
      <CTA>
        <BottomCTA
          heading="Trial Stile with Patty Luna"
          copy={
            <div>
              <p>
                Empower, support, repeat. That’s how we do it at Stile. In Victoria, that means
                teaming up with Patty Luna—a seasoned science educator and Melbourne-based
                curriculum specialist. With over two decades of experience, Patty brings science
                lessons to life.
              </p>
              <p>
                Recently at Ivanhoe Girls’ Grammar and a leader in professional learning with the
                Academy of Science, Patty is here to guide you through Stile—showing you just how
                transformative it can be.
              </p>
            </div>
          }
          image={
            <StaticImage
              src="../../../../stile-shared/assets/images/resources/patty_luna.png"
              alt="Stile classroom"
              quality={100}
            />
          }
          button={
            <>
              <Button
                className="setUpTrialButton"
                href="https://stileeducation.com/au/set-up-trial/"
                label={
                  <>
                    <span>Start a conversation with us</span>
                  </>
                }
              />
              <Button
                href="https://stileeducation.com/au/what-is-stile/whats-included/"
                label={
                  <>
                    <span>See what’s included in Stile</span>
                  </>
                }
              />
            </>
          }
        />
      </CTA>
    </VicLayout>
  );
}

export default VicLandingPage;
